/* eslint-disable */
import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { FormChange } from "@saleor/hooks/useForm";
import React from "react";
import TableCellAvatar, {
  AVATAR_MARGIN
} from "@saleor/components/TableCellAvatar";
import { FormattedMessage, useIntl } from "react-intl";
import {
  SortableTableBody,
  SortableTableRow
} from "@saleor/components/SortableTable";
import { maybe, renderCollection } from "../../../misc";
import { ListActions, ListProps2, ReorderAction } from "../../../types";
import { SaleDetails_sale } from "../../types/SaleDetails";
import { SaleDetailsPageFormData } from "../SaleDetailsPage";
import { SaleType } from "@saleor/types/globalTypes";

export interface DiscountSaleVariants extends ListProps2, ListActions {
  discount: SaleDetails_sale;
  onVariantAssign: () => void;
  onVariantUnassign: (id: string) => void;
  onChange: FormChange;
  data: SaleDetailsPageFormData;
  onVariantReorder: ReorderAction;
}

const useStyles = makeStyles(
  theme => ({
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colName: {
      paddingLeft: 0,
      width: 200
    },
    colNameLabel: {
      marginLeft: AVATAR_MARGIN + theme.spacing(3)
    },
    colPublished: {
      width: 150
    },
    colGrab: {
      width: 60
    },
    colType: {
      width: 130,
      padding: 0
    },
    colInventory: {
      width: 200
    },
    colPrice: {
      width: 200
    },
    colNum: {
      width: 200
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "DiscountSaleVariants" }
);

// const numberOfColumns = 8;

const DiscountSaleVariants: React.FC<DiscountSaleVariants> = props => {
  const {
    discount: sale,
    disabled,
    pageInfo,
    onRowClick,
    onPreviousPage,
    onVariantAssign,
    onVariantUnassign,
    onNextPage,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar,
    onVariantReorder,
    onChange,
    data
  } = props;
  const classes = useStyles(props);
  const numberOfColumns = SaleType.CUSTOM === data.type ? 8 : 6;

  const intl = useIntl();
  const hasSaleVariants = maybe(() =>
    sale.variants.edges.map(edge => edge.node)
  )?.length;

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Eligible SKU",
          description: "section header"
        })}
        toolbar={
          <Button color="primary" onClick={onVariantAssign}>
            <FormattedMessage
              defaultMessage="Assign SKU"
              description="button"
            />
          </Button>
        }
      />
      <ResponsiveTable>
        {SaleType.CUSTOM !== data.type ? (
          <colgroup>
            {hasSaleVariants ? <col /> : null}
            <col className={classes.colName} />
            <col className={classes.colType} />
            <col className={classes.colPublished} />
            <col className={classes.colActions} />
          </colgroup>
        ) : (
          <colgroup>
            <col className={classes.colGrab} />
            <col />
            <col className={classes.colName} />
            <col className={classes.colType} />
            <col className={classes.colType} />
            <col className={classes.colPrice} />
            <col className={classes.colPublished} />
            <col className={classes.colActions} />
          </colgroup>
        )}

        <TableHead
          colSpan={numberOfColumns}
          selected={selected}
          disabled={disabled}
          items={maybe(() =>
            SaleType.CUSTOM === data.type
              ? sale.customVariants.map(item => item.variant)
              : sale.variants.edges.map(edge => edge.node)
          )}
          toggleAll={toggleAll}
          toolbar={toolbar}
          dragRows={SaleType.CUSTOM === data.type}
        >
          <TableCell className={classes.colName}>
            <span className={hasSaleVariants ? classes.colNameLabel : ""}>
              <FormattedMessage defaultMessage="Product Name" />
            </span>
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="SKU" />
          </TableCell>
          <TableCell className={classes.colType}>
            <FormattedMessage defaultMessage="SKU Name" />
          </TableCell>
          {SaleType.CUSTOM === data.type ? (
            <>
              <TableCell className={classes.colInventory}>
                <FormattedMessage defaultMessage="Inventory" />
              </TableCell>
              <TableCell className={classes.colPrice}>
                <FormattedMessage defaultMessage="Discount Price" />
              </TableCell>
              {/* <TableCell className={classes.colNum}>
                <FormattedMessage defaultMessage="Display Order" />
              </TableCell> */}
            </>
          ) : null}
          <TableCell className={classes.colPublished}>
            <FormattedMessage
              defaultMessage="Published"
              description="product is published"
            />
          </TableCell>
          <TableCell className={classes.colActions} />
        </TableHead>
        {SaleType.CUSTOM === data.type ? (
          <TableFooter></TableFooter>
        ) : (
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={numberOfColumns}
                hasNextPage={
                  pageInfo && !disabled ? pageInfo.hasNextPage : false
                }
                onNextPage={onNextPage}
                hasPreviousPage={
                  pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                }
                onPreviousPage={onPreviousPage}
              />
            </TableRow>
          </TableFooter>
        )}
        {SaleType.CUSTOM === data.type ? (
          <SortableTableBody onSortEnd={onVariantReorder}>
            {renderCollection(
              maybe(() => sale?.customVariants),
              (variant, index) => {
                if (!variant) {
                  return undefined;
                }
                const isSelected = variant
                  ? isChecked(variant.variant.id)
                  : false;
                return (
                  <SortableTableRow
                    className={classes.tableRow}
                    index={index || 0}
                    hover={!!variant}
                    key={variant ? variant.variant.id : "skeleton"}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(variant.variant.id)}
                      />
                    </TableCell>
                    <TableCellAvatar
                      className={classes.colName}
                      thumbnail={maybe(() => variant.variant.thumbnail.url)}
                      onClick={
                        variant &&
                        onRowClick(variant.variant.product.id, variant.id)
                      }
                    >
                      {maybe<React.ReactNode>(
                        () => variant.variant.product.name,
                        <Skeleton />
                      )}
                    </TableCellAvatar>
                    <TableCell className={classes.colType}>
                      {maybe<React.ReactNode>(
                        () => variant.variant.sku,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colType}>
                      {maybe<React.ReactNode>(
                        () => variant.variant.name,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colInventory}>
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Sales Inventory",
                          description: "sale discount"
                        })}
                        onClick={event => event.stopPropagation()}
                        name={
                          ("variants." +
                            index +
                            ".usageLimit") as keyof SaleDetailsPageFormData
                        }
                        value={data.variants[index]?.usageLimit}
                        onChange={e => {
                          onChange(e);
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.colPrice}>
                      <TextField
                        disabled={disabled}
                        label={intl.formatMessage({
                          defaultMessage: "Discount Price",
                          description: "sale discount"
                        })}
                        onClick={event => event.stopPropagation()}
                        name={
                          ("variants." +
                            index +
                            ".discountPrice") as keyof SaleDetailsPageFormData
                        }
                        value={data.variants[index]?.discountPrice}
                        onChange={e => {
                          onChange(e);
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.colPublished}>
                      {variant && variant.variant.isPublished !== undefined ? (
                        <StatusLabel
                          label={
                            variant.variant.isPublished
                              ? intl.formatMessage({
                                  defaultMessage: "Published",
                                  description: "product is published"
                                })
                              : intl.formatMessage({
                                  defaultMessage: "Not published",
                                  description: "product is not published"
                                })
                          }
                          status={
                            variant.variant.isPublished ? "success" : "error"
                          }
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colActions}>
                      <IconButton
                        disabled={!variant || disabled}
                        onClick={event => {
                          event.stopPropagation();
                          onVariantUnassign(variant.variant.id);
                        }}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </SortableTableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage defaultMessage="No variants found" />
                  </TableCell>
                </TableRow>
              )
            )}
          </SortableTableBody>
        ) : (
          <TableBody>
            {renderCollection(
              maybe(() => sale?.variants?.edges?.map(edge => edge.node)),
              variant => {
                if (!variant) {
                  return undefined;
                }
                const isSelected = variant ? isChecked(variant.id) : false;
                return (
                  <TableRow
                    hover={!!variant}
                    key={variant ? variant.id : "skeleton"}
                    className={classes.tableRow}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(variant.id)}
                      />
                    </TableCell>
                    <TableCellAvatar
                      className={classes.colName}
                      thumbnail={maybe(() => variant.thumbnail.url)}
                      onClick={
                        variant && onRowClick(variant.product.id, variant.id)
                      }
                    >
                      {maybe<React.ReactNode>(
                        () => variant.product.name,
                        <Skeleton />
                      )}
                    </TableCellAvatar>
                    <TableCell className={classes.colType}>
                      {maybe<React.ReactNode>(() => variant.sku, <Skeleton />)}
                    </TableCell>
                    <TableCell className={classes.colType}>
                      {maybe<React.ReactNode>(() => variant.name, <Skeleton />)}
                    </TableCell>
                    <TableCell className={classes.colPublished}>
                      {variant && variant.isPublished !== undefined ? (
                        <StatusLabel
                          label={
                            variant.isPublished
                              ? intl.formatMessage({
                                  defaultMessage: "Published",
                                  description: "product is published"
                                })
                              : intl.formatMessage({
                                  defaultMessage: "Not published",
                                  description: "product is not published"
                                })
                          }
                          status={variant.isPublished ? "success" : "error"}
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colActions}>
                      <IconButton
                        disabled={!variant || disabled}
                        onClick={event => {
                          event.stopPropagation();
                          onVariantUnassign(variant.id);
                        }}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage defaultMessage="No variants found" />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        )}
      </ResponsiveTable>
    </Card>
  );
};
DiscountSaleVariants.displayName = "DiscountSaleVariants";
export default DiscountSaleVariants;
